import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface BookingInfo {
  id: number;
  status: string;
  reference: string;
  created_at: string;
  cartons: number;
  pieces: number;
  gross_weight: number;
  cbm: number;
  transport_type: number;
  transport_abbreviation: string;
}

interface BookingResponse {
  booking: {
    id: number;
    status: string;
    reference: string;
    created_at: string;
    transport_type: number;
    transport_types: {
      abbreviation: string;
    };
  };
  booking_id: number;
  cartons: number | null;
  pieces: number | null;
  gross_weight: number | null;
  cbm: number | null;
}

// Define live booking statuses
export const liveBookingStatuses = [
  "concept",
  "submitted",
  "approved",
  "rejected",
  "waiting_for_goods",
  "goods_received_origin",
  "in_transit",
  "customs_pending_clearance",
  "customs_cleared",
  "fyco",
  "delivery_planned",
  "out_for_delivery"
] as const;

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const cache = new Map<string, { data: BookingInfo[]; ts: number }>();

function cacheKey(ean: number, showLiveOnly: boolean): string {
  return `${ean}-${showLiveOnly}`;
}

export const useProductBookings = (ean: number | null, showLiveOnly: boolean = false) => {
  const [bookings, setBookings] = useState<BookingInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!ean) {
        setBookings([]);
        return;
      }

      const key = cacheKey(ean, showLiveOnly);
      const cached = cache.get(key);
      if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
        setBookings(cached.data);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data, error: supabaseError } = await supabase
          .from('booking_product')
          .select(`
            booking_id,
            cartons,
            pieces,
            gross_weight,
            cbm,
            booking:bookings!inner(
              id,
              status,
              reference,
              created_at,
              transport_type,
              transport_types:booking_transport_types(abbreviation)
            )
          `)
          .eq('ean', ean);

        if (supabaseError) throw supabaseError;

        // Initialize with empty array if data is undefined or null
        const typedData = ((data || []) as unknown) as BookingResponse[];
        
        // Transform the data to get unique bookings with their product details
        let uniqueBookings = Array.from(
          new Map(
            typedData
              .filter(item => item && item.booking) // Filter out any null/undefined items or items without booking
              .map(item => {
                const bookingWithDetails = {
                  ...item.booking,
                  transport_abbreviation: item.booking.transport_types?.abbreviation || 'AIR',
                  cartons: item.cartons || 0,
                  pieces: item.pieces || 0,
                  gross_weight: item.gross_weight || 0,
                  cbm: item.cbm || 0
                };
                return [item.booking.id, bookingWithDetails];
              })
          ).values()
        );
        
        // Filter bookings by status if showLiveOnly is true
        if (showLiveOnly) {
          uniqueBookings = uniqueBookings.filter(booking => 
            liveBookingStatuses.includes(booking.status as typeof liveBookingStatuses[number])
          );
        }

        // Sort bookings by created_at in descending order (newest first)
        uniqueBookings.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        cache.set(key, { data: uniqueBookings, ts: Date.now() });
        setBookings(uniqueBookings);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to fetch booking information');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [ean, showLiveOnly]);

  return { bookings, loading, error };
}; 